import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "./LaunchTable.css";
import "../informationCards/Card.css";
import { fetchNewLaunchInfo } from "../data/launches";
import { fetchRockets, fetchRocketFamilies } from "../data/rocketFamilies";
import { fetchPads } from "../data/pads";
import { fetchOrbits } from "../data/orbits";
import { fetchOperators } from "../data/operators";
import { getPhotoLocation, formatTime, numberWithCommas, repairMojibake, toTitleCase } from "../utils";

const cleanImagePath = (image) =>
  image ? String(image).replace("http://127.0.0.1:8000/", "").replace("http://127.0.0.1:8000", "") : "";

const getImageUrl = (image) => (image ? `${getPhotoLocation()}${cleanImagePath(image)}` : "");

const getOutcomeLabel = (outcome) => toTitleCase(outcome || "TBD").replace("Tbd", "TBD");

const getOutcomeClass = (outcome) => {
  const normalized = String(outcome || "").toLowerCase();

  if (normalized.includes("success")) {
    return "success";
  }

  if (normalized.includes("partial")) {
    return "partial";
  }

  if (normalized.includes("fail")) {
    return "failure";
  }

  return "";
};

const getStageFlightLabel = (stageAndRecovery) => {
  const stageName = stageAndRecovery.stage?.name || "Unknown";
  const flightNumber = Number(stageAndRecovery.num_flights || stageAndRecovery.stage?.num_launches);

  if (stageName === "Unknown") {
    return stageName;
  }

  if (Number.isFinite(flightNumber) && flightNumber > 0 && !new RegExp(`-${flightNumber}$`).test(stageName)) {
    return `${stageName}-${flightNumber}`;
  }

  return stageName;
};

const getStageResult = (stageAndRecovery, launchInFuture) => {
  if (launchInFuture) {
    return "Planned";
  }

  return getOutcomeLabel(stageAndRecovery.method_success || "TBD");
};

const formatPayloadMass = (mass) =>
  mass !== null && mass !== undefined
    ? `${numberWithCommas(mass)} kg (${numberWithCommas(Math.round(mass * 2.2))} lb)`
    : "Unknown";

const formatEnumLabel = (value) => toTitleCase(String(value || "Unknown").replace(/_/g, " "));

const getFamilyStatsPath = (family) => {
  const slug = String(family?.name || "").toLowerCase();

  if (slug === "falcon" || slug === "starship" || slug === "dragon") {
    return `/${slug}/stats/`;
  }

  return null;
};

const getProviderLaunchesPath = (operator) =>
  operator?.name ? `/launches/?query=${encodeURIComponent(operator.name)}` : null;

const isRecordStat = (text) =>
  /\b(to date|shortest|fastest|previous record)\b/i.test(String(text || ""));

const normalizeStatRows = (stats, { defaultImportant = false } = {}) =>
  (stats || [])
    .map((entry) => {
      const flagged = Array.isArray(entry) ? Boolean(entry[0]) : defaultImportant;
      const stat = Array.isArray(entry) ? entry[1] : entry;
      if (!stat) {
        return null;
      }
      const text = String(stat);
      return { important: flagged || isRecordStat(text), text };
    })
    .filter(Boolean);

const prepareStatRows = (rows, limit = 4) => {
  const seen = new Map();

  rows.forEach((row) => {
    const existing = seen.get(row.text);
    seen.set(row.text, existing ? { ...existing, important: existing.important || row.important } : row);
  });

  return Array.from(seen.values())
    .sort((a, b) => Number(b.important) - Number(a.important))
    .slice(0, limit);
};

const StatRows = ({ rows }) => (
  <div className="ld-stat-list">
    {prepareStatRows(rows).map((stat, index) => (
      <p className={stat.important ? "important" : ""} key={`${stat.text}-${index}`}>
        {stat.important ? <span className="ld-stat-star" aria-hidden="true">★</span> : null}
        <span>{stat.text}</span>
      </p>
    ))}
  </div>
);

const KeyStats = ({ rows }) => {
  const preparedRows = prepareStatRows(rows, 3);

  if (!preparedRows.length) {
    return null;
  }

  return (
    <div className="ld-key-stats">
      <p className="bt-section-label">Key stats</p>
      <div className="ld-key-stat-list">
        {preparedRows.map((stat, index) => (
          <p className={stat.important ? "important" : ""} key={`${stat.text}-${index}`}>
            {stat.important ? <span className="ld-stat-star" aria-hidden="true">★</span> : null}
            <span>{stat.text}</span>
          </p>
        ))}
      </div>
    </div>
  );
};

const RecordGroup = ({ title, label, rows, to }) => {
  if (!rows.length) {
    return null;
  }

  return (
    <div className="ld-record-group">
      {to ? (
        <Link to={to} className="bt-section-label ld-record-link">
          {label}
        </Link>
      ) : (
        <p className="bt-section-label">{label}</p>
      )}
      <h3>{title}</h3>
      <StatRows rows={rows} />
    </div>
  );
};

const DetailMetric = ({ label, value }) => (
  <div className="ld-metric">
    <span>{label}</span>
    <strong>{value || "Unknown"}</strong>
  </div>
);

const MetaChip = ({ to, children }) =>
  to ? (
    <Link to={to} className="ld-meta-link">
      {children}
    </Link>
  ) : (
    <span className="ld-meta-fact">{children}</span>
  );

const getStagePath = (stageAndRecovery) => {
  const stageId = stageAndRecovery?.stage?.id ?? (typeof stageAndRecovery?.stage === "number" ? stageAndRecovery.stage : null);
  return stageId ? `/stage/${stageId}/` : null;
};

const getZonePath = (stageAndRecovery) => {
  const zoneId =
    stageAndRecovery?.landing_zone?.id ??
    (typeof stageAndRecovery?.landing_zone === "number" ? stageAndRecovery.landing_zone : null);
  return zoneId ? `/recoveryzone/${zoneId}/` : null;
};

const LaunchInformation = () => {
  const { id } = useParams();
  const [launchInfo, setLaunchInfo] = useState({});
  const [rockets, setRockets] = useState([]);
  const [orbits, setOrbits] = useState([]);
  const [rocketFamilies, setRocketFamilies] = useState([]);
  const [pads, setPads] = useState([]);
  const [operators, setOperators] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [launchData, rocketData, padData, rocketFamilyData, orbitData, operatorData] = await Promise.all([
          fetchNewLaunchInfo(id),
          fetchRockets(),
          fetchPads("", {}),
          fetchRocketFamilies(),
          fetchOrbits(),
          fetchOperators(),
        ]);
        setLaunchInfo(launchData);
        setRockets(rocketData);
        setPads(padData);
        setRocketFamilies(rocketFamilyData);
        setOrbits(orbitData);
        setOperators(operatorData);
      } catch (err) {
        setError(err.message);
      }
    };

    fetchData();
  }, [id]);

  if (error) {
    return <div style={{ textAlign: "center", color: "white" }}>{error}</div>;
  }

  if (!launchInfo || !Object.keys(launchInfo).length) {
    return <div className="loading-indicator"></div>;
  }

  const rocketOnLaunch = rockets.find((rocket) => rocket.id === launchInfo.rocket);
  const spacecraftLaunch = launchInfo.spacecraft_on_launch?.[0] || null;
  const padOnLaunch = pads.find((pad) => pad.id === launchInfo.pad);
  const orbitOnLaunch = orbits.find((orbit) => orbit.id === launchInfo.orbit);
  const rocketFamilyOnLaunch = rocketFamilies.find((family) => family.id === rocketOnLaunch?.family);
  const operatorOnLaunch = operators.find((operator) => operator.id === rocketFamilyOnLaunch?.provider);
  const rocketInfoPath = getFamilyStatsPath(rocketFamilyOnLaunch);
  const providerInfoPath = getProviderLaunchesPath(operatorOnLaunch);
  const padInfoPath = padOnLaunch?.id ? `/pad/${padOnLaunch.id}/` : null;
  const launchInFuture = new Date(launchInfo.time) > new Date();
  const stageAndRecoveries = launchInfo.stage_and_recoveries || [];
  const outcomeLabel = getOutcomeLabel(launchInfo.launch_outcome);
  const formattedLaunchTime = formatTime(String(launchInfo.time || "").replace(/Z$/, ""));

  const missionHighlightRows = normalizeStatRows(launchInfo.significant_stats, { defaultImportant: true });
  const rocketRecordRows = normalizeStatRows(launchInfo.rocket_stats);
  const padRecordRows = normalizeStatRows(launchInfo.launch_pad_stats);
  const providerRecordRows = normalizeStatRows(launchInfo.launch_provider_stats);
  const stageRecordRows = stageAndRecoveries.flatMap((stageAndRecovery) => [
    ...normalizeStatRows(stageAndRecovery.stage_stats),
    ...normalizeStatRows(stageAndRecovery.landing_zone_stats),
  ]);
  const spacecraftRecordRows = normalizeStatRows(spacecraftLaunch?.spacecraft_stats);
  const heroImage = getImageUrl(
    launchInfo.image ||
      stageAndRecoveries[0]?.landing_zone?.image ||
      padOnLaunch?.image ||
      stageAndRecoveries[0]?.stage?.image ||
      rocketOnLaunch?.image
  );
  const keyStatRows = [
    ...missionHighlightRows,
    ...rocketRecordRows.filter((row) => row.important),
    ...padRecordRows.filter((row) => row.important),
    ...providerRecordRows.filter((row) => row.important),
    ...stageRecordRows.filter((row) => row.important),
  ];
  const primaryStagePath = stageAndRecoveries.length ? getStagePath(stageAndRecoveries[0]) : null;
  const spacecraftPath = spacecraftLaunch?.spacecraft?.id
    ? `/spacecraft/${spacecraftLaunch.spacecraft.id}/`
    : spacecraftLaunch?.spacecraft && typeof spacecraftLaunch.spacecraft === "number"
      ? `/spacecraft/${spacecraftLaunch.spacecraft}/`
      : null;
  const recordGroups = [
    { title: "Rocket records", label: rocketOnLaunch?.name || "Rocket", rows: rocketRecordRows, to: rocketInfoPath },
    { title: "Provider records", label: operatorOnLaunch?.name || "Provider", rows: providerRecordRows, to: providerInfoPath },
    { title: "Pad records", label: padOnLaunch?.nickname || "Pad", rows: padRecordRows, to: padInfoPath },
    {
      title: "Stage / recovery records",
      label: stageAndRecoveries[0] ? getStageFlightLabel(stageAndRecoveries[0]) : "Recovery",
      rows: stageRecordRows,
      to: primaryStagePath,
    },
    {
      title: "Spacecraft records",
      label: spacecraftLaunch?.spacecraft?.name || spacecraftLaunch?.spacecraft?.nickname || "Spacecraft",
      rows: spacecraftRecordRows,
      to: spacecraftPath,
    },
  ].filter((group) => group.rows.length);

  return (
    <div className="page-container-launch-page ld-page ld-page-simple">
      <Link to="/launches/" className="ld-back-link">
        &lt; Back to launches
      </Link>

      <section className="ld-simple-hero ld-panel">
        <div className="ld-simple-hero-image" style={{ backgroundImage: heroImage ? `url(${heroImage})` : undefined }}></div>
        <div className="ld-simple-hero-main">
          <p className="bt-section-label">Launch detail</p>
          <h1>{launchInfo.name}</h1>
          <div className="ld-hero-meta">
            <span className={`ld-status ${getOutcomeClass(launchInfo.launch_outcome)}`}>{outcomeLabel}</span>
            <MetaChip to={rocketInfoPath}>{rocketOnLaunch?.name || "Unknown"}</MetaChip>
            {stageAndRecoveries.map((stageAndRecovery) => {
              const stagePath = getStagePath(stageAndRecovery);
              const stageLabel = getStageFlightLabel(stageAndRecovery);
              if (!stageLabel || stageLabel === "Unknown") {
                return null;
              }

              return (
                <MetaChip to={stagePath} key={`hero-stage-${stageAndRecovery.id || stagePath || stageLabel}`}>
                  {stageLabel}
                </MetaChip>
              );
            })}
            <MetaChip to={padInfoPath}>{padOnLaunch?.nickname || "Unknown"}</MetaChip>
            <MetaChip to={null}>{repairMojibake(orbitOnLaunch?.nickname || orbitOnLaunch?.name) || "Unknown"}</MetaChip>
          </div>
          <KeyStats rows={keyStatRows} />
        </div>
        <div className="ld-simple-hero-facts">
          <DetailMetric label="Liftoff" value={formattedLaunchTime} />
          <DetailMetric label="Payload" value={formatPayloadMass(launchInfo.mass)} />
          <DetailMetric label="Customer" value={launchInfo.customer || "Unknown"} />
        </div>
      </section>

      <section className="ld-simple-grid">
        <section className="ld-panel ld-simple-card">
          <div className="ld-panel-head">
            <p className="bt-section-label">Stage and recovery</p>
            <h2>{stageAndRecoveries.length > 1 ? "Recovery stack" : "Recovery summary"}</h2>
          </div>
          {stageAndRecoveries.length ? (
            <div className="ld-recovery-stack">
              {stageAndRecoveries.map((stageAndRecovery) => {
                const stagePath = getStagePath(stageAndRecovery);
                const zonePath = getZonePath(stageAndRecovery);
                const stageLabel = getStageFlightLabel(stageAndRecovery);
                const zoneLabel =
                  stageAndRecovery.landing_zone?.nickname || stageAndRecovery.landing_zone?.name || "Unknown";
                const stageImage = getImageUrl(
                  stageAndRecovery.stage?.image || stageAndRecovery.landing_zone?.image || padOnLaunch?.image
                );
                const assetStyle = stageImage ? { backgroundImage: `url(${stageImage})` } : undefined;

                const identity = (
                  <>
                    <span className="ld-recovery-asset" style={assetStyle} aria-hidden="true" />
                    <span className="ld-recovery-title">
                      <p className="bt-section-label">{formatEnumLabel(stageAndRecovery.stage_position || "Stage")}</p>
                      <h3>{stageLabel}</h3>
                      <span>{stageAndRecovery.stage?.version || "Version unknown"}</span>
                    </span>
                  </>
                );

                return (
                  <div className="ld-recovery-card compact" key={stageAndRecovery.id}>
                    {stagePath ? (
                      <Link
                        to={stagePath}
                        className="ld-recovery-identity"
                        aria-label={`View ${stageLabel} booster history`}
                      >
                        {identity}
                      </Link>
                    ) : (
                      <div className="ld-recovery-identity">{identity}</div>
                    )}
                    <span className={`ld-status ${getOutcomeClass(stageAndRecovery.method_success)}`}>
                      {getStageResult(stageAndRecovery, launchInFuture)}
                    </span>
                    <div className="ld-recovery-metrics">
                      <DetailMetric label="Method" value={formatEnumLabel(stageAndRecovery.method)} />
                      <DetailMetric
                        label="Landing zone"
                        value={
                          zonePath ? (
                            <Link to={zonePath} className="ld-inline-link">
                              {zoneLabel}
                            </Link>
                          ) : (
                            zoneLabel
                          )
                        }
                      />
                      <DetailMetric label="Stage turnaround" value={stageAndRecovery.stage_turnaround || "N/A"} />
                      <DetailMetric label="Zone turnaround" value={stageAndRecovery.zone_turnaround || "N/A"} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="ld-empty">No stage or recovery data for this launch.</p>
          )}
        </section>

        <section className="ld-panel ld-simple-card">
          <div className="ld-panel-head">
            <p className="bt-section-label">Milestones</p>
            <h2>Mission milestones</h2>
          </div>
          {recordGroups.length ? (
            <div className="ld-record-stack">
              {recordGroups.map((group) => (
                <RecordGroup
                  title={group.title}
                  label={group.label}
                  rows={group.rows}
                  to={group.to}
                  key={`${group.label}-${group.title}`}
                />
              ))}
            </div>
          ) : (
            <p className="ld-empty">This launch does not have milestone records yet.</p>
          )}
        </section>
      </section>
    </div>
  );
};

export default LaunchInformation;
